import React, { useCallback, useMemo, useState } from "react";
import {
  CButton,
  CButtonGroup,
  CCol,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
  CFormInput,
  CFormLabel,
  CRow,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCalendar, cilCloudDownload, cilListFilter } from "@coreui/icons";

// === helpers (meses no formato YYYY-MM) ===
const toMonth = (value) => (value ? String(value).slice(0, 7) : null);

const addOneMonth = (ym) => {
  if (!ym) return null;
  const [y, m] = String(ym).split("-").map(Number);
  if (!y || !m) return null;
  const d = new Date(y, m, 1); // m (1..12) como índice 0..11 => mês seguinte
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Período por omissão da exportação: o período em execução (mês seguinte ao
 * "Executado até" até ao fim do projeto) ou, se não estiver definido, todo o
 * projeto — mesmo critério do atalho "Em execução" do FilterPanel.
 */
export const getDefaultTimesheetPeriod = (management) => {
  const start = toMonth(management?.start_date);
  const end = toMonth(management?.end_date);
  const next = addOneMonth(toMonth(management?.executed));
  if (next && (!end || next <= end) && (!start || next >= start)) {
    return { start: next, end };
  }
  return { start, end };
};

/**
 * Cartão de filtro temporal da exportação de timesheets (mesmo padrão do
 * cartão "Partilhar"). Abre com o período por omissão; `onExport({ start, end })`
 * deve devolver uma Promise. `onClose` é chamado em "Cancelar" e após sucesso.
 * Usado dentro do dropdown "Exportar Dados" (interno) e do botão próprio do
 * portal externo.
 */
export const TimesheetExportForm = ({
  management,
  onExport,
  onClose,
  onExportingChange,
}) => {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(() => {
    const initial = getDefaultTimesheetPeriod(management);
    return { start: initial.start || "", end: initial.end || "" };
  });

  const minMonth = toMonth(management?.start_date) || undefined;
  const maxMonth = toMonth(management?.end_date) || undefined;

  const applyPeriod = useCallback((next) => {
    setPeriod({ start: next.start || "", end: next.end || "" });
    setError(null);
  }, []);

  const setBusy = (busy) => {
    setExporting(busy);
    onExportingChange?.(busy);
  };

  const invalidMessage = useMemo(() => {
    if (!period.start || !period.end)
      return "Indique o mês de início e de fim.";
    if (period.start > period.end)
      return "O mês de início não pode ser posterior ao mês de fim.";
    if (
      (minMonth && period.end < minMonth) ||
      (maxMonth && period.start > maxMonth)
    )
      return "O período está fora das datas do projeto.";
    return null;
  }, [period, minMonth, maxMonth]);

  const handleExport = () => {
    if (invalidMessage || !onExport) return;
    setBusy(true);
    setError(null);
    Promise.resolve(onExport({ start: period.start, end: period.end }))
      .then(() => onClose?.())
      .catch((err) =>
        setError(err?.message || "Não foi possível gerar a timesheet."),
      )
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="d-flex align-items-start gap-2 mb-3">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-2 bg-primary-subtle text-primary flex-shrink-0"
          style={{ width: 32, height: 32 }}
        >
          <CIcon icon={cilCalendar} />
        </span>
        <div>
          <div className="fw-semibold lh-sm">Exportar Timesheet</div>
          <div className="text-body-secondary small">
            Escolha o período a exportar.
          </div>
        </div>
      </div>

      <CButtonGroup size="sm" className="w-100 mb-3">
        <CButton
          color="secondary"
          variant="outline"
          disabled={exporting}
          onClick={() => applyPeriod(getDefaultTimesheetPeriod(management))}
        >
          <CIcon icon={cilCalendar} className="me-1" />
          Em execução
        </CButton>
        <CButton
          color="secondary"
          variant="outline"
          disabled={exporting}
          onClick={() => applyPeriod({ start: minMonth, end: maxMonth })}
        >
          <CIcon icon={cilListFilter} className="me-1" />
          Todo o projeto
        </CButton>
      </CButtonGroup>

      <CRow className="g-2 mb-2">
        <CCol xs={6}>
          <CFormLabel htmlFor="timesheetExportStart" className="small mb-1">
            De
          </CFormLabel>
          <CFormInput
            id="timesheetExportStart"
            type="month"
            size="sm"
            min={minMonth}
            max={maxMonth}
            value={period.start}
            disabled={exporting}
            onChange={(e) => applyPeriod({ ...period, start: e.target.value })}
          />
        </CCol>
        <CCol xs={6}>
          <CFormLabel htmlFor="timesheetExportEnd" className="small mb-1">
            Até
          </CFormLabel>
          <CFormInput
            id="timesheetExportEnd"
            type="month"
            size="sm"
            min={minMonth}
            max={maxMonth}
            value={period.end}
            disabled={exporting}
            onChange={(e) => applyPeriod({ ...period, end: e.target.value })}
          />
        </CCol>
      </CRow>

      <div
        className="text-body-secondary bg-body-tertiary rounded-2 px-2 py-1"
        style={{ fontSize: "0.75rem" }}
      >
        Só são incluídos os técnicos com datas no projeto e horas imputadas
        neste período. Os meses fora do período ficam ocultos.
      </div>

      {(invalidMessage || error) && (
        <div className="mt-2 text-danger" style={{ fontSize: "0.75rem" }}>
          {error || invalidMessage}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
        <CButton
          color="secondary"
          variant="outline"
          size="sm"
          disabled={exporting}
          onClick={() => onClose?.()}
        >
          Cancelar
        </CButton>
        <CButton
          color="primary"
          size="sm"
          disabled={exporting || !!invalidMessage}
          onClick={handleExport}
        >
          {exporting && (
            <CSpinner component="span" size="sm" className="me-2" />
          )}
          Exportar
        </CButton>
      </div>
    </>
  );
};

/**
 * Botão "Exportar Timesheet" com o cartão de filtro temporal (portal externo).
 */
const TimesheetExportButton = ({
  management,
  onExport,
  disabled = false,
  size,
  className = "",
}) => {
  const [visible, setVisible] = useState(false);
  const [exporting, setExporting] = useState(false);

  return (
    <CDropdown
      alignment="end"
      visible={visible}
      // CoreUI 5 só expõe onShow/onHide para sincronizar o estado controlado.
      onShow={() => setVisible(true)}
      onHide={() => setVisible(false)}
      autoClose="outside"
    >
      <CDropdownToggle
        caret={false}
        color="primary"
        size={size}
        className={`d-flex align-items-center text-nowrap rounded-2 ${className}`}
        disabled={disabled || !onExport || !management}
        title="Exportar timesheets com filtro temporal"
      >
        {exporting ? (
          <CSpinner component="span" size="sm" className="me-2" />
        ) : (
          <CIcon icon={cilCloudDownload} className="me-2" />
        )}
        Exportar Timesheet
      </CDropdownToggle>

      <CDropdownMenu className="p-3 shadow-sm" style={{ minWidth: "380px" }}>
        {/* montado só quando aberto => repõe o período por omissão */}
        {visible && (
          <TimesheetExportForm
            management={management}
            onExport={onExport}
            onClose={() => setVisible(false)}
            onExportingChange={setExporting}
          />
        )}
      </CDropdownMenu>
    </CDropdown>
  );
};

export default TimesheetExportButton;
